<template>
  <div class="card m-3 text-center">
    <h4 class="card-header">{{ t('2fa-title') }}</h4>
    <div class="d-flex card-body justify-content-center">
      <div class="sms-code-form">
        <p class="hint">
          {{ t('2fa-code-sent-to') }}
          <strong>{{ maskedPhone }}</strong>
        </p>

        <!-- Sześć osobnych pól na cyfry. Auto-tab przy wpisaniu, paste rozdzielany. -->
        <div class="code-inputs" @paste.prevent="onPaste">
          <input
            v-for="(_, idx) in 6"
            :key="idx"
            ref="digitRefs"
            type="text"
            inputmode="numeric"
            autocomplete="one-time-code"
            maxlength="1"
            class="code-digit"
            :class="{ 'is-invalid': hasError }"
            :value="digits[idx]"
            :disabled="isVerifying"
            @input="onDigitInput($event, idx)"
            @keydown="onDigitKeydown($event, idx)"
            @focus="onDigitFocus($event)"
          />
        </div>

        <!-- Countdown ważności kodu -->
        <p class="countdown" v-if="secondsLeft > 0">
          {{ t('2fa-code-expires-in') }}
          <strong>{{ formattedCountdown }}</strong>
        </p>
        <p class="countdown text-danger" v-else>
          {{ t('2fa-code-expired') }}
        </p>

        <div class="actions">
          <button
            type="button"
            class="btn btn-outline-secondary"
            :disabled="!canVerify"
            @click="onVerify"
          >
            <span v-show="isVerifying" class="spinner-border spinner-border-sm me-1"></span>
            {{ t('2fa-verify') }}
          </button>

          <button
            type="button"
            class="btn btn-link btn-sm"
            :disabled="resendCooldown > 0 || isResending"
            @click="onResend"
          >
            <span v-show="isResending" class="spinner-border spinner-border-sm me-1"></span>
            {{ resendCooldown > 0
              ? t('2fa-resend-in', { seconds: resendCooldown })
              : t('2fa-resend') }}
          </button>

          <button
            type="button"
            class="btn btn-link btn-sm text-muted"
            @click="onCancel"
          >
            {{ t('2fa-cancel') }}
          </button>
        </div>

      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAuthStore } from '@/stores/auth.store';
import { useNotificationStore } from '@/stores/notification.store';

const { t } = useI18n();
const router = useRouter();
const authStore = useAuthStore();
const notify = useNotificationStore();

const digits = ref(['', '', '', '', '', '']);
const digitRefs = ref([]);
const isVerifying = ref(false);
const isResending = ref(false);
const hasError = ref(false);

// TTL i resend cooldown wyliczane z `expiresAt` / `resendInSeconds` w challenge z BE —
// front nie hardcoduje tych wartości, tylko liczy z timestampu wystawienia.
const secondsLeft = ref(0);
const resendCooldown = ref(0);

let ttlTimer = null;
let resendTimer = null;

// Defense-in-depth: blokuj wyciek do referrera (analogicznie do EnterSmsCodeView).
const REFERRER_META_ID = '2fa-code-no-referrer';

onMounted(() => {
  const pending = authStore.pendingTwoFactor;
  // Wejście wprost na URL bez przejścia przez login = brak stanu. Odsyłamy na /login.
  if (!pending || pending.requiresPhoneSetup) {
    router.replace(pending?.requiresPhoneSetup ? '/login/2fa/setup-phone' : '/login');
    return;
  }

  if (!document.getElementById(REFERRER_META_ID)) {
    const meta = document.createElement('meta');
    meta.id = REFERRER_META_ID;
    meta.name = 'referrer';
    meta.content = 'no-referrer';
    document.head.appendChild(meta);
  }

  syncCountdownsFromChallenge();
  startTtlCountdown();
  startResendCooldown();
  if (pending.dispatchFailed) {
    showError(t('2fa-dispatch-failed'));
  }
  nextTick(() => digitRefs.value[0]?.focus());
});

onBeforeUnmount(() => {
  document.getElementById(REFERRER_META_ID)?.remove();
  if (ttlTimer) clearInterval(ttlTimer);
  if (resendTimer) clearInterval(resendTimer);
});

const fullCode = computed(() => digits.value.join(''));
const canVerify = computed(
  () => fullCode.value.length === 6 && !isVerifying.value && secondsLeft.value > 0
);

const maskedPhone = computed(() => authStore.pendingTwoFactor?.challenge?.phoneMasked ?? '');

const formattedCountdown = computed(() => {
  const m = Math.floor(secondsLeft.value / 60);
  const s = secondsLeft.value % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
});

function syncCountdownsFromChallenge() {
  const ch = authStore.pendingTwoFactor?.challenge;
  if (!ch) return;
  const expiresMs = new Date(ch.expiresAt).getTime();
  const now = Date.now();
  secondsLeft.value = Math.max(0, Math.floor((expiresMs - now) / 1000));
  resendCooldown.value = Number(ch.resendInSeconds) || 0;
}

function startTtlCountdown() {
  if (ttlTimer) clearInterval(ttlTimer);
  ttlTimer = setInterval(() => {
    if (secondsLeft.value > 0) secondsLeft.value -= 1;
  }, 1000);
}

function startResendCooldown() {
  if (resendTimer) clearInterval(resendTimer);
  resendTimer = setInterval(() => {
    if (resendCooldown.value > 0) resendCooldown.value -= 1;
  }, 1000);
}

// Treść komunikatu idzie do globalnych powiadomień; lokalnie zostaje tylko
// `hasError`, bo od niego zależy czerwona obwódka pól z kodem.
function showError(msg) {
  hasError.value = true;
  notify.error(msg);
}

function showInfo(msg) {
  hasError.value = false;
  notify.success(msg);
}

function clearError() {
  hasError.value = false;
}

function onDigitInput(event, idx) {
  const v = event.target.value.replace(/\D/g, '').slice(0, 1);
  digits.value[idx] = v;
  event.target.value = v;
  clearError();
  if (v && idx < 5) {
    digitRefs.value[idx + 1]?.focus();
  }
}

function onDigitKeydown(event, idx) {
  if (event.key === 'Backspace' && !digits.value[idx] && idx > 0) {
    digitRefs.value[idx - 1]?.focus();
  } else if (event.key === 'ArrowLeft' && idx > 0) {
    digitRefs.value[idx - 1]?.focus();
  } else if (event.key === 'ArrowRight' && idx < 5) {
    digitRefs.value[idx + 1]?.focus();
  } else if (event.key === 'Enter' && canVerify.value) {
    onVerify();
  }
}

function onDigitFocus(event) {
  event.target.select();
}

function onPaste(event) {
  const text = (event.clipboardData?.getData('text') || '').replace(/\D/g, '').slice(0, 6);
  if (!text) return;
  for (let i = 0; i < 6; i++) {
    digits.value[i] = text[i] || '';
  }
  clearError();
  const lastIdx = Math.min(text.length, 6) - 1;
  nextTick(() => digitRefs.value[Math.min(lastIdx + 1, 5)]?.focus());
}

async function onVerify() {
  if (!canVerify.value) return;
  isVerifying.value = true;
  clearError();
  try {
    const ok = await authStore.verifyTwoFactorCode(fullCode.value);
    if (!ok) {
      showError(t('2fa-code-invalid-or-expired'));
      digits.value = ['', '', '', '', '', ''];
      nextTick(() => digitRefs.value[0]?.focus());
      return;
    }
    // Sukces — store sam przekierowuje na strona docelową.
  } catch {
    showError(t('2fa-code-invalid-or-expired'));
  } finally {
    isVerifying.value = false;
  }
}

async function onResend() {
  if (resendCooldown.value > 0 || isResending.value) return;
  isResending.value = true;
  try {
    await authStore.resendTwoFactorCode();
    syncCountdownsFromChallenge();
    showInfo(t('2fa-code-resent'));
  } catch {
    showError(t('2fa-resend-failed'));
  } finally {
    isResending.value = false;
  }
}

async function onCancel() {
  // Wylogowanie wyczyści sesję BE (z flagą _2fa_pending) i zwróci na /login.
  authStore.clearPendingTwoFactor();
  authStore.logout();
}
</script>

<style scoped>
.card-body {
  background: var(--surface-secondary);
}
.sms-code-form {
  width: 460px;
  max-width: 100%;
}
.hint {
  font-size: var(--font-size-sm);
  margin-bottom: 1rem;
}
.code-inputs {
  display: flex;
  gap: var(--spacing-sm);
  justify-content: center;
  margin: 1.25rem 0;
}
.code-digit {
  width: 48px;
  height: 56px;
  text-align: center;
  font-size: 24px;
  font-weight: var(--font-weight-medium);
  border: 1px solid var(--border-default);
  background: var(--surface-primary);
}
.code-digit:focus {
  outline: none;
  border-color: var(--text-secondary);
  box-shadow: 0 0 0 0.25rem rgba(100, 101, 104, 0.25);
}
.code-digit.is-invalid {
  border-color: var(--text-danger);
  background: var(--status-error-soft);
}
.countdown {
  font-size: var(--font-size-sm);
  margin-bottom: 1rem;
}
.actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}
.actions .btn {
  border-radius: var(--control-radius);
  padding: 4px 16px;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
}
.actions .btn-outline-secondary {
  border: 1px solid grey;
  color: var(--text-primary);
}
.actions .btn-link {
  color: var(--text-secondary);
  font-weight: var(--font-weight-regular);
}
</style>
